import { graphqlClient } from '@/lib/graphql-client';
import { callAttendanceWorkflow } from '@/modules/time-attendance/attendance/services/attendance.service';
import type { OrderBy } from '@/lib/graphql-query-builder';
import {
  buildGetAttendanceCorrectionsQuery,
  buildGetAttendancesQuery,
  DEFAULT_ROSTER_ORDER,
} from '../graphql/queries';
import { buildGetEmployeesQuery } from '@/modules/shared/hr-lookups/graphql/queries';
import { attendanceDateClause } from '@/modules/time-attendance/attendance/services/attendance-rules';
import { ImportFormat, ImportRow } from '../utils/attendance-import';
import {
  AttendanceCorrectionsPagingResult,
  AttendanceRecord,
  AttendancesPagingResult,
  RosterRow,
  CorrectionStatus,
  GetAttendanceCorrectionsParams,
  GetAttendancesParams,
} from '../types/manage-attendance.types';

export const getAttendances = async (
  params: GetAttendancesParams
): Promise<{ Attendances: AttendancesPagingResult }> => {
  const { pageNo, pageSize, filter = {}, order } = params;
  const result = (await graphqlClient.query({
    query: buildGetAttendancesQuery(filter, pageNo, pageSize, order),
  })) as { Attendances: AttendancesPagingResult };
  // Sorted by the server (newest day first unless `order` says otherwise); breaks normalized like
  // My Attendance.
  return {
    Attendances: {
      ...result.Attendances,
      items: result.Attendances.items.map((item) => ({
        ...item,
        BreakTimeDetails: item.BreakTimeDetails ?? [],
      })),
    },
  };
};

/**
 * One organization day for every employee in scope (legacy "Manage Attendance" view): a page of
 * employees, each with that day's record or none. `employeeFilter` carries the department condition,
 * keyword and column filters; resigned employees are left out. `order` sorts the employees
 * (UserProfileDetail fields), A to Z by name by default.
 */
export const getAttendanceRoster = async (params: {
  day: string;
  employeeFilter: Record<string, unknown>;
  pageNo: number;
  pageSize: number;
  order?: OrderBy[];
}): Promise<{ rows: RosterRow[]; totalCount: number }> => {
  const employees = (await graphqlClient.query({
    query: buildGetEmployeesQuery(
      { ...params.employeeFilter, IsResigned: { $ne: true } },
      params.pageNo,
      params.pageSize,
      params.order ?? DEFAULT_ROSTER_ORDER
    ),
  })) as { Employees: { totalCount: number; items: RosterRow['employee'][] } };
  const people = employees.Employees.items;
  const userIds = people.map((person) => person.UserId).filter(Boolean);
  let records: AttendanceRecord[] = [];
  if (userIds.length > 0) {
    const result = (await graphqlClient.query({
      query: buildGetAttendancesQuery(
        { UserId: { $in: userIds }, AttendanceDate: attendanceDateClause(params.day) },
        1,
        userIds.length
      ),
    })) as { Attendances: AttendancesPagingResult };
    records = result.Attendances.items;
  }
  return {
    totalCount: employees.Employees.totalCount,
    rows: people.map((employee) => ({
      employee,
      record: records.find((record) => record.UserId === employee.UserId) ?? null,
    })),
  };
};

export const getAttendanceCorrections = async (
  params: GetAttendanceCorrectionsParams
): Promise<{ AttendanceCorrections: AttendanceCorrectionsPagingResult }> => {
  const { pageNo, pageSize, filter = {}, order } = params;
  const result = (await graphqlClient.query({
    query: buildGetAttendanceCorrectionsQuery(filter, pageNo, pageSize, order),
  })) as { AttendanceCorrections: AttendanceCorrectionsPagingResult };
  return result; // sorted by the server, latest corrected day first unless `order` says otherwise
};

/**
 * Approve, reject and waive each go through their own workflow, which re-checks the reviewer
 * (REVIEW_CORRECTION, or WAIVE_CORRECTION_FINE for a waiver; admin, or head of the correction's
 * department; never their own request), claims the request atomically (a request two reviewers act on
 * at once is decided once), applies an approval to the attendance day (creating the day's record when
 * there is none, the fine kept once per day), records the reviewer from the session and notifies the
 * employee in the app and by mail. Sources: blocks/workflows/attendance/.
 */
const REVIEW_WEBHOOKS = {
  Approve: {
    url: import.meta.env.VITE_ATTENDANCE_CORRECTION_APPROVE_WEBHOOK_URL ?? '',
    envName: 'VITE_ATTENDANCE_CORRECTION_APPROVE_WEBHOOK_URL',
    failureCode: 'ATTENDANCE_CORRECTION_APPROVE_FAILED',
  },
  Reject: {
    url: import.meta.env.VITE_ATTENDANCE_CORRECTION_REJECT_WEBHOOK_URL ?? '',
    envName: 'VITE_ATTENDANCE_CORRECTION_REJECT_WEBHOOK_URL',
    failureCode: 'ATTENDANCE_CORRECTION_REJECT_FAILED',
  },
  Waive: {
    url: import.meta.env.VITE_ATTENDANCE_FINE_WAIVE_WEBHOOK_URL ?? '',
    envName: 'VITE_ATTENDANCE_FINE_WAIVE_WEBHOOK_URL',
    failureCode: 'ATTENDANCE_FINE_WAIVE_FAILED',
  },
};

type ReviewAction = keyof typeof REVIEW_WEBHOOKS;

const runReview = (itemId: string, action: ReviewAction, note?: string) => {
  const { url, envName, failureCode } = REVIEW_WEBHOOKS[action];
  return callAttendanceWorkflow<{ Action: ReviewAction; Notified: boolean }>(
    url,
    envName,
    { itemId, ...(note ? { note } : {}) },
    failureCode
  );
};

export const reviewAttendanceCorrection = (params: {
  itemId: string;
  status: Exclude<CorrectionStatus, 'Pending'>;
  note?: string;
}) => runReview(params.itemId, params.status === 'Approved' ? 'Approve' : 'Reject', params.note);

/** Zeroes an approved correction's fine (WAIVE_CORRECTION_FINE, checked by the workflow). */
export const waiveCorrectionFine = (itemId: string) => runReview(itemId, 'Waive');

/** How many requests at once a bulk review sends: each is a full workflow run. */
const BULK_REVIEW_CONCURRENCY = 3;

export interface BulkReviewResult {
  succeeded: string[];
  /** Each refused request with the workflow's translation key (or English message). */
  failed: { itemId: string; error: unknown }[];
}

/**
 * Approves or rejects several requests: the same workflow once per request, a few at a time, so every
 * request is checked on its own (a request another reviewer decided meanwhile is refused, the rest go
 * through). Never throws; the result lists what failed.
 */
export const bulkReviewAttendanceCorrections = async (params: {
  itemIds: string[];
  status: Exclude<CorrectionStatus, 'Pending'>;
  note?: string;
}): Promise<BulkReviewResult> => {
  const result: BulkReviewResult = { succeeded: [], failed: [] };
  const queue = [...params.itemIds];
  const worker = async () => {
    for (let itemId = queue.shift(); itemId; itemId = queue.shift()) {
      try {
        await reviewAttendanceCorrection({ itemId, status: params.status, note: params.note });
        result.succeeded.push(itemId);
      } catch (error) {
        result.failed.push({ itemId, error });
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(BULK_REVIEW_CONCURRENCY, queue.length) }, worker)
  );
  return result;
};

const ATTENDANCE_IMPORT_WEBHOOK_URL = import.meta.env.VITE_ATTENDANCE_IMPORT_WEBHOOK_URL ?? '';

/** Rows per ATTENDANCE_IMPORT call (the workflow accepts at most 200). */
const IMPORT_CHUNK_SIZE = 200;

export interface ImportSkippedRow {
  Row: number;
  EmployeeId: string;
  Day: string;
  /** Translation key suffix, e.g. DAY_ALREADY_RECORDED. */
  Reason: string;
}

export interface AttendanceImportResult {
  saved: number;
  skipped: ImportSkippedRow[];
  overtimeEntries: number;
}

/**
 * Sends parsed rows to ATTENDANCE_IMPORT in chunks. A door log's rows are one per employee and day, so
 * a chunk never splits a day; a device export's rows of one employee and day are kept in one chunk.
 * The workflow decides everything (scope, rules, days already recorded); the result lists every row
 * that was not saved with its reason.
 */
export const importAttendance = async (params: {
  format: ImportFormat;
  fileName: string;
  rows: ImportRow[];
}): Promise<AttendanceImportResult> => {
  const byDay = new Map<string, ImportRow[]>();
  for (const row of params.rows) {
    const key = `${row.employeeId}|${row.day}`;
    byDay.set(key, [...(byDay.get(key) ?? []), row]);
  }
  const chunks: ImportRow[][] = [];
  let current: ImportRow[] = [];
  for (const dayRows of byDay.values()) {
    if (current.length + dayRows.length > IMPORT_CHUNK_SIZE && current.length > 0) {
      chunks.push(current);
      current = [];
    }
    current.push(...dayRows);
  }
  if (current.length > 0) chunks.push(current);

  const result: AttendanceImportResult = { saved: 0, skipped: [], overtimeEntries: 0 };
  for (const chunk of chunks) {
    try {
      const answer = await callAttendanceWorkflow<{
        Saved: number;
        Skipped: ImportSkippedRow[];
        OvertimeEntries: number;
      }>(
        ATTENDANCE_IMPORT_WEBHOOK_URL,
        'VITE_ATTENDANCE_IMPORT_WEBHOOK_URL',
        // The workflow accepts a file name of up to 200 characters.
        { format: params.format, fileName: params.fileName.slice(0, 200), rows: chunk },
        'ATTENDANCE_IMPORT_FAILED'
      );
      result.saved += answer.Data?.Saved ?? 0;
      result.skipped.push(...(answer.Data?.Skipped ?? []));
      result.overtimeEntries += answer.Data?.OvertimeEntries ?? 0;
    } catch {
      // A refused or failed chunk: its rows are reported; chunks already saved stay counted.
      result.skipped.push(
        ...chunk.map((row) => ({
          Row: row.row,
          EmployeeId: row.employeeId,
          Day: row.day,
          Reason: 'CHUNK_FAILED',
        }))
      );
    }
  }
  return result;
};
